import React from 'react'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

library.add(fas)

const formatEuros = cents => {
  const value = (cents || 0) / 100
  return value.toLocaleString(I18n.locale || 'fr', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })
}

const stepRange = (step, currency) => {
  const min = formatEuros(step.minAmountCents)
  if (step.maxAmountCents == null || step.maxAmountCents === '') {
    return I18n.t('validation_workflows.how.range_above', { min: `${min} ${currency}` })
  }
  const max = formatEuros(step.maxAmountCents)
  return I18n.t('validation_workflows.how.range_between', { min: `${min} ${currency}`, max: `${max} ${currency}` })
}

const stepApproverNames = (step, usersById) => {
  const names = (step.approverIds || [])
    .map(id => (usersById[id] || {}).name)
    .filter(Boolean)
  if (names.length === 0) return I18n.t('validation_workflows.how.no_approvers_yet')
  return names.join(` ${I18n.t('validation_workflows.how.or_separator')} `)
}

const HowItWorks = ({ workflow, users, categories, objectType, currencySymbol }) => {
  const usersById   = (users || []).reduce((acc, u) => { acc[u.id] = u; return acc }, {})
  const steps       = (workflow.approvalSteps || []).slice().sort((a, b) => a.position - b.position)
  const trigger     = formatEuros(workflow.triggerAmountCents)
  const currency    = currencySymbol || ''
  const objectLabel = I18n.t(`validation_workflows.object_label_plural.${objectType}`)
  const amountBased = objectType !== 'workorder'

  const categoryIds   = workflow.categoryIds || []
  const categoriesById = (categories || []).reduce((acc, c) => { acc[c.id] = c; return acc }, {})
  const categoryNames = categoryIds
    .map(id => (categoriesById[id] || {}).name)
    .filter(Boolean)
  const categoryList  = categoryNames.join(', ')

  return (
    <div className='bg-blue-50 border border-blue-200 rounded-md px-5 py-4'>
      <div className='flex items-center gap-2 text-blue-900 mb-2'>
        <FontAwesomeIcon icon='comment-alt' />
        <strong>{I18n.t('validation_workflows.how.title')}</strong>
      </div>
      <div className='text-gray-700'>
        <p className='my-2'>{(() => {
          const amount = `${trigger} ${currency}`
          if (amountBased && categoryNames.length) {
            return I18n.t('validation_workflows.how.applies_to_amount_and_categories', { object: objectLabel, amount, categories: categoryList })
          }
          if (amountBased) {
            return I18n.t('validation_workflows.how.applies_to', { object: objectLabel, amount })
          }
          if (categoryNames.length) {
            return I18n.t('validation_workflows.how.applies_to_categories', { object: objectLabel, categories: categoryList })
          }
          return I18n.t('validation_workflows.how.applies_to_all', { object: objectLabel })
        })()}</p>
        {steps.length === 0 ? (
          <p className='text-muted'>{I18n.t('validation_workflows.how.no_steps_yet')}</p>
        ) : (
          <>
            <p className='my-2'>{I18n.t('validation_workflows.how.intro_steps', { count: steps.length })}</p>
            <ol className='my-2 ml-5 p-0 list-decimal'>
              {steps.map((step, idx) => (
                <li key={step.id || `tmp-${idx}`} className='py-1'>
                  <strong>{step.name || I18n.t('validation_workflows.how.unnamed_step')}</strong>
                  {amountBased && (
                    <>
                      {' '}
                      <span className='text-muted'>{`(${stepRange(step, currency)})`}</span>
                    </>
                  )}
                  {' — '}
                  <span>{stepApproverNames(step, usersById)}</span>
                </li>
              ))}
            </ol>
          </>
        )}
        <p className='my-2'>{workflow.selfApproval
          ? I18n.t('validation_workflows.how.self_approval_on')
          : I18n.t('validation_workflows.how.self_approval_off')
        }</p>
        <p className='my-2'>{workflow.allowEditAfterValidation
          ? I18n.t('validation_workflows.how.allow_edit_on')
          : I18n.t('validation_workflows.how.allow_edit_off')
        }</p>
      </div>
    </div>
  )
}

export default HowItWorks
